"use client";
import SubjectIcon from "@/app/components/logos/SubjectIcon";
import ResourceIcon from "@/app/components/logos/resources/ResourceIcon";
import { capAll, getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Anchor, Badge, Box, Button, Text, Title } from "@mantine/core";
import { BannerLinkItem } from "@utils/routing";
import { buildSubjectSlug, getIBOSubjectLink } from "@utils/routing";
import clsx from "clsx";
import Link from "next/link";
import classes from "./ResourceBanner.module.css";

export default function ResourceBanner({
  subject,
  currentResourceMessage,
  suggestedLink,
  linksHeading,
  links,
}: {
  subject: string;
  currentResourceMessage: string;
  suggestedLink?: BannerLinkItem | null;
  linksHeading: string;
  links: BannerLinkItem[];
}) {
  const subjectName = capAll(subject);
  const subjectColorIndex = getSubjectColorIndex(subject);
  const linksWithSuggestion = toLinksWithSuggestedBadge(links, suggestedLink);
  return (
    <Box
      className={classes.BannerContainer}
      style={
        {
          "--rb-c6": `var(--mantine-color-${subjectColorIndex}-6)`,
          "--rb-c9": `var(--mantine-color-${subjectColorIndex}-9)`,
        } as React.CSSProperties
      }
    >
      <Box className={classes.BannerDecor} aria-hidden>
        {[classes.bannerIconLeft, classes.bannerIconRight].map(
          (bannerIconClass, index) => (
            <SubjectIcon
              key={index}
              className={clsx(classes.bannerIcon, bannerIconClass)}
              subjectColorIndex={subjectColorIndex}
            />
          )
        )}
      </Box>
      <Title order={3} className={classes.title}>
        Next Up
      </Title>
      <Text className={classes.subTitle}>
        You have completed the {currentResourceMessage} for{" "}
        <Anchor component={Link} href={buildSubjectSlug(subject)}>
          IB {subjectName}
        </Anchor>{" "}
        - continue with related resources below or explore the full{" "}
        <Anchor component={Link} href={getIBOSubjectLink(subject)}>
          IB {subjectName} course
        </Anchor>
         {" "}from the IBO.
      </Text>
      <Text className={classes.linksHeading}>{linksHeading}</Text>
      <Box className={classes.linkGrid}>
        {linksWithSuggestion.map((link) => (
          <Button
            key={`${link.href}-${link.label}`}
            component={Link}
            href={link.href}
            className={classes.linkButton}
            size="md"
            color={"darkblue"}
            variant="white"
          >
            <span className={classes.linkInner}>
              {link.resource && link.resource !== "all" ? (
                <ResourceIcon
                  resource={link.resource}
                  size={28}
                  backgroundColor={subjectColorIndex}
                  className={classes.resourceIcon}
                />
              ) : (
                <SubjectIcon
                  subjectColorIndex={subjectColorIndex}
                  className={classes.resourceIcon}
                />
              )}
              <span>{link.label}</span>
            </span>
            {link.badge && (
              <Badge className={classes.linkBadge} variant="filled" size="xs">
                Suggested
              </Badge>
            )}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

function toLinksWithSuggestedBadge(
  links: BannerLinkItem[],
  suggestedLink?: BannerLinkItem | null
) {
  const linksByHref = new Map(links.map((link) => [link.href, { ...link }]));

  if (!suggestedLink) {
    return Array.from(linksByHref.values());
  }

  if (linksByHref.has(suggestedLink.href)) {
    const existing = linksByHref.get(suggestedLink.href)!;
    existing.badge = "Suggested";
    linksByHref.set(suggestedLink.href, existing);
    return Array.from(linksByHref.values());
  }

  return [{ ...suggestedLink, badge: "Suggested" }, ...Array.from(linksByHref.values())];
}
